/**
 * UUID utility functions for dev-tools
 * Reuses existing s12-helpers.ts functions and adds dev-tools specific functionality
 */
import { DocPrefix } from "../../../constants/index";
import {
  toS12ID,
  toUUID,
  validateS12ID,
  validateUUID,
} from "../../../helpers/s12-helpers";

/**
 * Check if a field name suggests it's an ID field that might need UUID conversion
 */
export const isIdField = (fieldName: string): boolean => {
  const lowerName = fieldName.toLowerCase();
  return (
    lowerName.includes("userid") ||
    lowerName.includes("user_id") ||
    lowerName.includes("orgid") ||
    lowerName.includes("org_id") ||
    lowerName.includes("organisationid") ||
    lowerName.includes("organisation_id") ||
    lowerName.includes("organizationid") ||
    lowerName.includes("organization_id") ||
    lowerName.includes("roleid") ||
    lowerName.includes("role_id") ||
    lowerName === "id"
  );
};

/**
 * Check if a value looks like a prefixed ID (e.g., role_xxx, user_xxx)
 * Extended pattern to support more prefixes than s12-helpers validateS12ID
 */
export const isPrefixedId = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;

  // First check with s12-helpers validation for standard prefixes
  if (validateS12ID(value)) return true;

  // Extended pattern for additional prefixes that might be used in dev-tools
  const extendedPrefixPattern =
    /^(org|organisation|organization|template|action|evidence|location|responseset|audit|ntfmsg)_[a-f0-9]{32}$/i;
  return extendedPrefixPattern.test(value);
};

/**
 * Check if a value looks like a UUID
 * Delegates to s12-helpers validateUUID
 */
export const isUuid = (value: string): boolean => {
  if (!value || typeof value !== "string") return false;
  return validateUUID(value);
};

/**
 * Convert prefixed ID to UUID format
 * Uses s12-helpers toUUID with fallback for extended prefixes
 */
export const prefixedIdToUuid = (value: string): string => {
  if (!isPrefixedId(value)) return value;

  try {
    // Try s12-helpers first for standard prefixes
    return toUUID(value);
  } catch {
    // Fallback for extended prefixes not supported by s12-helpers
    const match = value.match(/^[a-z]+_([a-f0-9]{32})$/i);
    if (!match) return value;

    const hex = match[1];
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }
};

/**
 * Convert UUID to prefixed ID format
 * Uses s12-helpers toS12ID with proper DocPrefix enum values
 */
export const uuidToPrefixedId = (value: string, prefix = "role"): string => {
  if (!isUuid(value)) return value;

  // Map common prefixes to DocPrefix enum values
  const prefixToDocPrefix: Record<string, DocPrefix> = {
    user: DocPrefix.PrefixUser,
    role: DocPrefix.PrefixRole,
    template: DocPrefix.PrefixTemplate,
    action: DocPrefix.PrefixAction,
    evidence: DocPrefix.PrefixEvidence,
    location: DocPrefix.PrefixLocation,
    audit: DocPrefix.PrefixAudit,
    responseset: DocPrefix.PrefixResponseSet,
    ntfmsg: DocPrefix.PrefixNotification,
  };

  // Default org/organisation/organization to role for compatibility
  const normalizedPrefix =
    prefix === "org" || prefix === "organisation" || prefix === "organization"
      ? "role"
      : prefix;

  const docPrefix = prefixToDocPrefix[normalizedPrefix];

  if (docPrefix) {
    try {
      return toS12ID(docPrefix, value);
    } catch {
      // Fallback if s12-helpers fails
      const hex = value.replace(/-/g, "");
      return `${prefix}_${hex}`;
    }
  } else {
    // For unsupported prefixes, use fallback implementation
    const hex = value.replace(/-/g, "");
    return `${prefix}_${hex}`;
  }
};

/**
 * Get the prefix from a prefixed ID
 * e.g., role_xxx -> "role", user_xxx -> "user"
 */
export const extractPrefix = (value: string): string | null => {
  if (!isPrefixedId(value)) return null;

  const match = value.match(/^([a-z]+)_[a-f0-9]{32}$/i);
  return match ? match[1] : null;
};

/**
 * Guess the appropriate prefix based on field name
 */
export const guessPrefix = (fieldName: string): string => {
  const lowerName = fieldName.toLowerCase();

  if (lowerName.includes("user")) return "user";
  if (lowerName.includes("org")) return "org";
  if (lowerName.includes("role")) return "role";

  // Default to role for generic IDs
  return "role";
};

/**
 * Toggle between prefixed ID and UUID format
 */
export const toggleIdFormat = (value: string, fieldName: string): string => {
  // Only toggle if it's an ID field
  if (!isIdField(fieldName)) {
    return value;
  }

  if (isPrefixedId(value)) {
    // Convert to UUID
    return prefixedIdToUuid(value);
  } else if (isUuid(value)) {
    // Convert to prefixed ID
    const prefix = guessPrefix(fieldName);
    return uuidToPrefixedId(value, prefix);
  }

  // Return unchanged if not recognized format
  return value;
};
